import { Fragment } from 'react'

import { cn } from '#/shared/lib/utils'

/** http(s) URLs plus bare `www.` hosts — the two shapes people actually paste
 *  into an SMS. Deliberately not `file://` etc.: browsers refuse to open those
 *  from a web page, so a dead link would be worse than plain text. */
const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<>"']+/gi

type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'link'; text: string; href: string }

/** Sentence punctuation trailing a pasted URL ("see https://x.io/sign.") is
 *  almost never part of it — strip it so the link resolves. */
function trimTrailingPunctuation(url: string): string {
  return url.replace(/[.,!?;:]+$/, '')
}

/** Splits free text into plain runs and link runs, in original order. */
function splitLinks(text: string): Segment[] {
  const segments: Segment[] = []
  let last = 0
  for (const match of text.matchAll(URL_PATTERN)) {
    const link = trimTrailingPunctuation(match[0])
    if (match.index > last)
      segments.push({ kind: 'text', text: text.slice(last, match.index) })
    segments.push({
      kind: 'link',
      text: link,
      // A scheme-less `www.` host needs one for the browser to leave the app.
      href: link.startsWith('www.') ? `https://${link}` : link,
    })
    last = match.index + link.length
  }
  if (last < text.length)
    segments.push({ kind: 'text', text: text.slice(last) })
  return segments
}

/**
 * Plain text with any URLs rendered as clickable links opening in a new tab.
 * Links inherit the surrounding text color unless `linkClassName` overrides it,
 * and swallow their click so a tappable ancestor (e.g. a chat bubble) doesn't
 * also fire.
 */
export function LinkifiedText({
  text,
  linkClassName,
}: {
  text: string
  linkClassName?: string
}) {
  const segments = splitLinks(text)
  return (
    <>
      {segments.map((segment, index) =>
        segment.kind === 'link' ? (
          <a
            key={index}
            href={segment.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            className={cn(
              'cursor-pointer underline decoration-current/40 underline-offset-2 transition-colors hover:decoration-current',
              linkClassName,
            )}
          >
            {segment.text}
          </a>
        ) : (
          <Fragment key={index}>{segment.text}</Fragment>
        ),
      )}
    </>
  )
}
